import apiClient from './apiClient';

const TOKEN_KEY = 'uplift_access_token';
const SESSION_USER_KEY = 'uplift_session_user';

export const AUTH_CHANGED_EVENT = 'uplift-auth-changed';
export const PROFILE_UPDATED_EVENT = 'uplift-profile-updated';

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/** Cached user from last login or /auth/me — used for immediate nav (role) after sign-in. */
export function getCachedSessionUser() {
  if (!getStoredToken()) return null;
  try {
    const raw = sessionStorage.getItem(SESSION_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function setCachedSessionUser(user) {
  if (user) {
    sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
  } else {
    sessionStorage.removeItem(SESSION_USER_KEY);
  }
}

export function notifyAuthChanged() {
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

/** Prefer full /auth/me profile; fall back to cached session user for sidebar nav. */
export function hydrateMeFromCache() {
  if (!getStoredToken()) return null;
  return getCachedSessionUser();
}

export function logout() {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(SESSION_USER_KEY);
  notifyAuthChanged();
}

function cacheFromLoginUser(user) {
  if (!user) return;
  setCachedSessionUser({
    id: user.id,
    username: user.username,
    email: user.email,
    role: user.role,
    status: user.status,
  });
}

function cacheFromMe(me) {
  if (!me) return;
  setCachedSessionUser({
    id: me.id,
    username: me.username,
    email: me.email,
    role: me.role,
    status: me.status,
    firstName: me.firstName,
    lastName: me.lastName,
  });
}

/**
 * @param {{ username: string, password: string }} credentials
 */
export async function login(credentials) {
  const { data } = await apiClient.post('/auth/login', credentials);
  if (data.accessToken) setStoredToken(data.accessToken);
  if (data.user) cacheFromLoginUser(data.user);
  notifyAuthChanged();
  return data;
}

/**
 * @param {object} payload — register fields matching backend RegisterRequest
 */
export async function register(payload) {
  const { data } = await apiClient.post('/auth/register', payload);
  return data;
}

/** @param {string} email */
export async function requestPasswordReset(email) {
  const { data } = await apiClient.post('/auth/forgot-password', { email });
  return data;
}

/** @param {{ token: string, password: string }} body */
export async function resetPassword(body) {
  const { data } = await apiClient.post('/auth/reset-password', body);
  return data;
}

export async function fetchMe() {
  const { data } = await apiClient.get('/auth/me');
  cacheFromMe(data);
  return data;
}
